import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAcceptInvitation } from '../hooks/useAcceptInvitation';
import { useInvitationInfo } from '../hooks/useInvitationInfo';
import { AcceptInvitationRequest } from '../models/User';

interface AcceptInvitationFormProps {
  className?: string;
}

export const AcceptInvitationForm: React.FC<AcceptInvitationFormProps> = ({ className = '' }) => {
  const { t } = useTranslation('usersModule');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const acceptInvitationMutation = useAcceptInvitation();

  const token = searchParams.get('token');
  const { data: invitationInfo, isLoading: isLoadingInfo, error: infoError } = useInvitationInfo(token);

  const [formData, setFormData] = useState<AcceptInvitationRequest & { confirmPassword: string }>({
    token: token || '',
    name: '',
    password: '',
    confirmPassword: '',
  });

  const [fieldErrors, setFieldErrors] = useState<{
    token?: string;
    name?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  // Actualizar email si viene en la información de la invitación
  useEffect(() => {
    if (invitationInfo?.email && !formData.name) {
      // Pre-llenar el nombre con la parte del email antes del @ si no hay nombre
      const emailName = invitationInfo.email.split('@')[0];
      setFormData((prev) => ({ ...prev, name: emailName }));
    }
  }, [formData.name, invitationInfo]);

  // Validación de campos individuales
  const validateField = (name: keyof typeof formData, value: string): string | undefined => {
    switch (name) {
      case 'token':
        if (!value.trim()) {
          return t('acceptInvitation.errors.tokenRequired', 'El token es requerido');
        }
        return undefined;

      case 'name':
        // Solo validar si requires_name es true
        if (invitationInfo?.requires_name && !value.trim()) {
          return t('acceptInvitation.errors.nameRequired', 'El nombre es requerido');
        }
        return undefined;

      case 'password':
        // Solo validar si requires_password es true
        if (invitationInfo?.requires_password && !value.trim()) {
          return t('acceptInvitation.errors.passwordRequired', 'La contraseña es requerida');
        }
        if (value.trim() && value.trim().length < 8) {
          return t('acceptInvitation.errors.passwordMinLength', 'La contraseña debe tener al menos 8 caracteres');
        }
        return undefined;

      case 'confirmPassword':
        // Solo validar si requires_password es true
        if (invitationInfo?.requires_password && !value.trim()) {
          return t('acceptInvitation.errors.confirmPasswordRequired', 'Por favor confirma tu contraseña');
        }
        if (value && value !== formData.password) {
          return t('acceptInvitation.errors.passwordsDoNotMatch', 'Las contraseñas no coinciden');
        }
        return undefined;

      default:
        return undefined;
    }
  };

  // Manejo de cambios en inputs
  const handleInputChange = (name: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Limpiar error del campo si existe
    if (fieldErrors[name as keyof typeof fieldErrors]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // Validar confirmPassword cuando cambia password
    if (name === 'password' && formData.confirmPassword) {
      const confirmError = validateField('confirmPassword', formData.confirmPassword);
      setFieldErrors((prev) => ({
        ...prev,
        confirmPassword: confirmError,
      }));
    }
  };

  // Validación completa del formulario
  const validateForm = (): boolean => {
    if (!invitationInfo) return false;

    const errors: typeof fieldErrors = {};

    const tokenError = validateField('token', formData.token);
    if (tokenError) errors.token = tokenError;

    if (invitationInfo.requires_name) {
      const nameError = validateField('name', formData.name);
      if (nameError) errors.name = nameError;
    }

    if (invitationInfo.requires_password) {
      const passwordError = validateField('password', formData.password);
      if (passwordError) errors.password = passwordError;

      const confirmPasswordError = validateField('confirmPassword', formData.confirmPassword);
      if (confirmPasswordError) errors.confirmPassword = confirmPasswordError;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Manejo del submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!invitationInfo || !validateForm()) return;

    try {
      const requestData: AcceptInvitationRequest = {
        token: formData.token,
      };

      // Solo incluir campos si son requeridos
      if (invitationInfo.requires_password) {
        requestData.password = formData.password;
      }
      if (invitationInfo.requires_name) {
        requestData.name = formData.name.trim();
      }

      await acceptInvitationMutation.mutateAsync(requestData);
    } catch (error) {
      // Los errores se manejan automáticamente en el hook
      console.error('Error en aceptar invitación:', error);
    }
  };

  // Manejo de blur para validación inmediata
  const handleBlur = (name: keyof typeof formData) => {
    const error = validateField(name, formData[name]);
    if (error) {
      setFieldErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  // Si no hay token, mostrar error
  if (!token) {
    return (
      <div className={`max-w-96 ${className}`}>
        <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
          <p className="text-sm text-destructive">
            {t('acceptInvitation.errors.tokenRequired', 'Token no encontrado en la URL')}
          </p>
        </div>
      </div>
    );
  }

  // Si está cargando la información de la invitación
  if (isLoadingInfo) {
    return (
      <div className={`max-w-96 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="ml-2 text-sm text-complementary-text">
            {t('acceptInvitation.loading', 'Cargando información de la invitación...')}
          </span>
        </div>
      </div>
    );
  }

  // Si hay error al cargar la información de la invitación
  if (infoError) {
    const errorMessage =
      (infoError as any)?.response?.data?.message ||
      (infoError as any)?.message ||
      t('acceptInvitation.errors.tokenInvalid', 'Token inválido o expirado');

    return (
      <div className={`max-w-96 ${className}`}>
        <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
          <p className="text-sm text-destructive mb-4">{errorMessage}</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/login')} className="w-full">
            {t('acceptInvitation.buttons.goToLogin', 'Ir al inicio de sesión')}
          </Button>
        </div>
      </div>
    );
  }

  // Si no hay información de invitación
  if (!invitationInfo) {
    return (
      <div className={`max-w-96 ${className}`}>
        <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
          <p className="text-sm text-destructive">
            {t('acceptInvitation.errors.tokenInvalid', 'Token inválido o expirado')}
          </p>
        </div>
      </div>
    );
  }

  const isFormValid = () => {
    if (!invitationInfo) return false;
    const nameValid = !invitationInfo.requires_name || formData.name.trim().length > 0;
    const passwordValid = !invitationInfo.requires_password || formData.password.trim().length >= 8;
    const confirmPasswordValid = !invitationInfo.requires_password || formData.confirmPassword === formData.password;
    return nameValid && passwordValid && confirmPasswordValid;
  };

  return (
    <div className={`max-w-96 ${className}`}>
      {/* Información de la invitación */}
      {invitationInfo && (
        <div className="mb-6 p-4 bg-accent/20 border border-card-border rounded-lg">
          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-medium text-foreground">{t('acceptInvitation.info.email', 'Email')}:</span>{' '}
              <span className="text-complementary-text">{invitationInfo.email}</span>
            </div>
            {invitationInfo.user_exists && (
              <div className="text-xs text-complementary-text">
                {t('acceptInvitation.info.userExists', 'Este usuario ya existe en el sistema')}
              </div>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Error general */}
        {acceptInvitationMutation.error && (
          <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
            <p className="text-sm text-destructive">
              {acceptInvitationMutation.error.message ||
                t('acceptInvitation.errors.unexpected', 'Error inesperado al aceptar la invitación')}
            </p>
          </div>
        )}

        {/* Campo Nombre - Solo si requires_name es true */}
        {invitationInfo.requires_name && (
          <div className="space-y-2">
            <label htmlFor="accept-name" className="text-sm font-medium text-foreground">
              {t('acceptInvitation.fields.name', 'Nombre')} *
            </label>
            <Input
              id="accept-name"
              type="text"
              placeholder={t('acceptInvitation.fields.namePlaceholder', 'Tu nombre completo')}
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              onBlur={() => handleBlur('name')}
              className={`${fieldErrors.name ? 'border-destructive' : ''}`}
              autoFocus
              autoComplete="name"
              data-testid="accept-invitation-name"
            />
            {fieldErrors.name && <p className="text-sm text-destructive">{fieldErrors.name}</p>}
          </div>
        )}

        {/* Campo Contraseña - Solo si requires_password es true */}
        {invitationInfo.requires_password && (
          <>
            <div className="space-y-2">
              <label htmlFor="accept-password" className="text-sm font-medium text-foreground">
                {t('acceptInvitation.fields.password', 'Contraseña')} *
              </label>
              <Input
                id="accept-password"
                type="password"
                placeholder={t('acceptInvitation.fields.passwordPlaceholder', 'Mínimo 8 caracteres')}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                onBlur={() => handleBlur('password')}
                className={`${fieldErrors.password ? 'border-destructive' : ''}`}
                autoFocus={!invitationInfo.requires_name}
                autoComplete="new-password"
                data-testid="accept-invitation-password"
              />
              {fieldErrors.password && <p className="text-sm text-destructive">{fieldErrors.password}</p>}
            </div>

            {/* Campo Confirmar Contraseña */}
            <div className="space-y-2">
              <label htmlFor="accept-confirm-password" className="text-sm font-medium text-foreground">
                {t('acceptInvitation.fields.confirmPassword', 'Confirmar Contraseña')} *
              </label>
              <Input
                id="accept-confirm-password"
                type="password"
                placeholder={t('acceptInvitation.fields.confirmPasswordPlaceholder', 'Confirma tu contraseña')}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                onBlur={() => handleBlur('confirmPassword')}
                className={`${fieldErrors.confirmPassword ? 'border-destructive' : ''}`}
                autoComplete="new-password"
                data-testid="accept-invitation-confirm-password"
              />
              {fieldErrors.confirmPassword && <p className="text-sm text-destructive">{fieldErrors.confirmPassword}</p>}
            </div>
          </>
        )}

        {/* Botón de envío */}
        <div className="pt-4">
          <Button
            type="submit"
            className="w-full"
            disabled={acceptInvitationMutation.isPending || !isFormValid()}
            data-testid="accept-invitation-submit"
          >
            {acceptInvitationMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('acceptInvitation.buttons.submitting', 'Aceptando invitación...')}
              </>
            ) : (
              t('acceptInvitation.buttons.submit', 'Aceptar Invitación')
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AcceptInvitationForm;
